import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const STATUS_LABEL = {
  draft: "Draft", pending_approval: "Pending", open: "Open", applied: "Applied",
  assigned: "Assigned", in_progress: "In Progress", review: "Review",
  rework_required: "Rework", blocked: "Blocked", completed: "Completed",
  scored: "Scored", closed: "Closed", cancelled: "Cancelled",
};

export default function BulkTasks() {
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    // Load ALL statuses for admin bulk view
    const statuses = ["draft", "pending_approval", "open", "applied", "assigned", "in_progress", "review", "rework_required", "blocked", "completed", "scored", "closed", "cancelled"];
    const results = await Promise.all(statuses.map((s) => api.get(`/tasks?status=${s}&limit=200`).catch(() => ({ data: [] }))));
    setTasks(results.flatMap((r) => r.data));
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === tasks.length) setSelected(new Set());
    else setSelected(new Set(tasks.map((t) => t.task_id)));
  };

  const doAction = async (action) => {
    if (selected.size === 0) {
      toast.error("Select tasks first");
      return;
    }
    if (action === "delete" && !window.confirm(`Delete ${selected.size} task(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk-tasks", { task_ids: Array.from(selected), action });
      toast.success(`${action} applied to ${data.affected} task(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="bulk-tasks-page">
      <div className="border border-border bg-background p-3 flex flex-wrap gap-2 items-center">
        <div className="text-xs uppercase tracking-wider text-zinc-500">
          {selected.size} of {tasks.length} selected
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => doAction("reopen")} disabled={busy || selected.size === 0} className="rounded-sm" data-testid="bulk-reopen-btn">Reopen</Button>
          <Button variant="outline" size="sm" onClick={() => doAction("close")} disabled={busy || selected.size === 0} className="rounded-sm" data-testid="bulk-close-btn">Close</Button>
          <Button variant="outline" size="sm" onClick={() => doAction("cancel")} disabled={busy || selected.size === 0} className="rounded-sm" data-testid="bulk-cancel-btn">Cancel</Button>
          <Button variant="outline" size="sm" onClick={() => doAction("delete")} disabled={busy || selected.size === 0} className="rounded-sm text-red-600 border-red-200" data-testid="bulk-delete-btn">Delete</Button>
        </div>
      </div>

      <div className="border border-border bg-background overflow-x-auto">
        <table className="w-full text-sm" data-testid="bulk-tasks-table">
          <thead className="border-b border-border">
            <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <th className="text-left py-2 px-3 w-8">
                <Checkbox checked={tasks.length > 0 && selected.size === tasks.length} onCheckedChange={toggleAll} data-testid="bulk-select-all" />
              </th>
              <th className="text-left py-2 px-3">Title</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-right py-2 px-3">Effort</th>
              <th className="text-right py-2 px-3">Credits</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.task_id} className="border-b border-border" data-testid={`bulk-row-${t.task_id}`}>
                <td className="py-2 px-3">
                  <Checkbox checked={selected.has(t.task_id)} onCheckedChange={() => toggle(t.task_id)} data-testid={`bulk-check-${t.task_id}`} />
                </td>
                <td className="py-2 px-3 truncate max-w-[420px]">{t.title}</td>
                <td className="py-2 px-3"><span className="text-xs uppercase tracking-wider">{STATUS_LABEL[t.status] || t.status}</span></td>
                <td className="py-2 px-3 text-right font-mono">{t.effort_hours}h</td>
                <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{t.credit_cost}</td>
              </tr>
            ))}
            {tasks.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-sm text-zinc-500">No tasks in the system yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
